import type { ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { Link, useMatch } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useStores } from "@/stores";
import { BillingStatusContext, useBillingStatus } from "./BillingStatusContext";
import { useBuildingSubscription } from "./hooks";
import type { BuildingSubscription } from "./types";

function subscriptionForBuilding(buildingId: number, subscription?: BuildingSubscription) {
  if (!Number.isSafeInteger(buildingId) || buildingId <= 0) return undefined;
  return subscription?.building_id === buildingId ? subscription : undefined;
}

function PendingBuildingActivation() {
  const { t } = useLanguage();
  return (
    <div className="mx-auto max-w-2xl space-y-4 p-6">
      <Alert>
        <AlertDescription>{t.billing.plans.activationPending}</AlertDescription>
      </Alert>
      <Button asChild variant="outline">
        <Link to="/billing/invoices">{t.billing.banners.viewInvoice}</Link>
      </Button>
      <Button asChild variant="outline">
        <Link to="/billing">{t.billing.nav.group}</Link>
      </Button>
    </div>
  );
}

/** Scope content controls to the effective plan; organization billing stays intact. */
export const BuildingBillingScopeProvider = observer(function BuildingBillingScopeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const billing = useBillingStatus();
  const { projectFolderStore } = useStores();
  const match = useMatch("/buildings/:id/*");
  const buildingId = Number(match?.params.id);
  const enabled = Number.isSafeInteger(buildingId) && buildingId > 0;
  const subscription = useBuildingSubscription(buildingId, enabled);
  const buildingSubscription = subscriptionForBuilding(buildingId, subscription.data);
  const buildingProjectIds = new Set(
    enabled && billing.orgId
      ? projectFolderStore.getProjectOptions(billing.orgId, buildingId).map((project) => project.id)
      : []
  );
  if (buildingSubscription?.activation_pending) return <PendingBuildingActivation />;
  return (
    <BillingStatusContext.Provider
      value={{
        ...billing,
        buildingSubscription,
        buildingProjectIds,
      }}
    >
      {children}
    </BillingStatusContext.Provider>
  );
});
